/**
 * Staff sign-in — email OTP, on its own rail.
 *
 * Staff are a SEPARATE table with no crossover with candidate accounts, their own OTP path and
 * their own JWT audience. Nothing here touches candidate auth, and a candidate token could not
 * open this console if it tried (the admin rail validates the `:staff` audience and rejects it).
 *
 * *** THE NEUTRAL RESPONSE ***
 * `POST /otp/request` answers identically for a staff address and a stranger's — one shape, one
 * constant value, byte-identical, deliberately carrying no field that could vary with existence.
 * The API went to some trouble for that, and THIS SCREEN IS WHERE IT CAN BE THROWN AWAY. So:
 *
 *   - The copy after a request says "if that address can receive a code" — never "we've sent you
 *     a code", which asserts the address exists.
 *   - There is no "unknown email" state, because the API does not tell us that and we must not
 *     imply we know. Not on request, and not on verify either: `invalid_code` covers a wrong
 *     code, an unknown address AND a deactivated one — the service already folded those three
 *     together, and un-folding them here would rebuild the exact oracle it collapsed.
 *   - The step advances to "enter your code" REGARDLESS. A stranger who types an address gets
 *     the same screen a staff member does, and learns nothing.
 *
 * If a future change makes this screen say something a stranger could use to confirm a guess,
 * that change is wrong no matter how helpful it feels.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { requestStaffOtp, verifyStaffOtp, errorCode } from '@/lib/admin-api';
import { useStaffSession } from '@/auth/session';
import { getCaptchaToken } from '@/auth/captcha';
import { ThreadMark } from '@/components/ThreadMark';
import { Button } from '@/components/ui/button';
import { TextField } from '@/components/ui/field';

type Step = 'email' | 'code';

/** Verify-step error copy. Coarse by design — see the module comment. */
function verifyErrorMessage(code: string | null): string {
  switch (code) {
    case 'code_expired':
      return 'That code has expired. Ask for a new one.';
    case 'too_many_attempts':
    case 'rate_limited':
      return 'Too many attempts. Wait a moment and try again.';
    default:
      // invalid_code and everything else. Says nothing about whether the address is a staff
      // address — that is the point, not an omission.
      return 'That code didn’t work. Check it and try again.';
  }
}

export function Login() {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const signIn = useStaffSession((state) => state.signIn);
  const accessToken = useStaffSession((state) => state.accessToken);
  const location = useLocation();
  const codeRef = useRef<HTMLInputElement>(null);

  // Move focus to the code field when the step advances — the keyboard path should not require
  // hunting for the next input.
  useEffect(() => {
    if (step === 'code') codeRef.current?.focus();
  }, [step]);

  if (accessToken !== null) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? '/dashboard'} replace />;
  }

  async function onRequest(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setPending(true);
    try {
      await requestStaffOtp(email.trim(), await getCaptchaToken());
      // Advance unconditionally on success. The response is neutral; branching on it is not
      // possible and must not be simulated.
      setStep('code');
    } catch (caught) {
      const code = errorCode(caught);
      setError(
        code === 'invalid_email'
          ? 'That doesn’t look like an email address.'
          : code === 'rate_limited'
            ? 'Too many requests. Wait a moment and try again.'
            : 'Couldn’t send a code just now. Try again in a moment.',
      );
    } finally {
      setPending(false);
    }
  }

  async function onVerify(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setPending(true);
    try {
      const tokens = await verifyStaffOtp(email.trim(), code.trim());
      signIn(tokens);
    } catch (caught) {
      setError(verifyErrorMessage(errorCode(caught)));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex min-h-svh flex-col items-center justify-center px-5 py-10">
      <div className="page-mount w-full max-w-[400px]">
        <div className="mb-6 flex flex-col items-center text-center">
          <ThreadMark className="h-6 w-auto" />
          <h1 className="mt-4 font-display text-[20px] font-semibold tracking-[-0.02em] text-ink">
            Staff sign-in
          </h1>
          <p className="mt-1.5 font-body text-[12.5px] leading-relaxed text-ink-70">
            By My Word admin. Staff accounts only.
          </p>
        </div>

        <div className="rounded-lg border border-line bg-surface p-5 shadow-sm">
          {step === 'email' ? (
            <form onSubmit={onRequest} noValidate>
              <TextField
                label="Work email"
                type="email"
                name="email"
                autoComplete="username"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                error={error ?? undefined}
              />
              <div className="mt-4">
                <Button type="submit" size="block" disabled={pending || email.trim() === ''}>
                  {pending ? 'Sending…' : 'Send me a code'}
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={onVerify} noValidate>
              {/* Neutral by construction: "if that address can receive a code". Never "we sent
                  you a code" — that would assert the address exists. */}
              <p role="status" className="mb-3 font-body text-[12.5px] leading-relaxed text-ink-70">
                If that address can receive a code, one has been sent. Enter it below.
              </p>
              <TextField
                ref={codeRef}
                label="Sign-in code"
                name="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(event) => setCode(event.target.value)}
                error={error ?? undefined}
              />
              <div className="mt-4 flex flex-col gap-2">
                <Button type="submit" size="block" disabled={pending || code.trim() === ''}>
                  {pending ? 'Checking…' : 'Sign in'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="block"
                  disabled={pending}
                  onClick={() => {
                    setStep('email');
                    setCode('');
                    setError(null);
                  }}
                >
                  Use a different address
                </Button>
              </div>
            </form>
          )}
        </div>

        <p className="mt-4 text-center font-body text-[11px] leading-relaxed text-ink-70">
          Your session is held in memory only and ends when you close or reload this tab.
        </p>
      </div>
    </div>
  );
}
